import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { Tooltip } from 'react-tooltip';

/**
 * Per-cleaner time on site, and the honest combined total (owner, 2026-09-02).
 *
 * The booking's actual window (first report → completed) is the default for
 * every cleaner, adjusted by what the driver ledger actually recorded:
 *
 *   - Dropped off more than 15 minutes AFTER the actual start → her clock
 *     starts at DROP-OFF + 10 MINUTES (the walk from the car to the unit is
 *     nobody's fault).
 *   - Picked up more than 15 minutes BEFORE the actual finish → her clock
 *     stops at the pickup; the time after she left counts for nobody.
 *
 * The owner's own example: one lady from 12:00, the second dropped 13:50 so
 * her clock runs from 14:00; both finish 16:00 → 4h + 2h = 6h combined.
 * Within the 15-minute tolerance the shared window stands — vans idle,
 * clocks drift, and phantom minutes are noise, not information.
 */
export default function CleanerDurations({ bookingId, bookingStart, startedIso, finishedIso, isInProcess, cleanerNames, onCombined }) {
    const [stopRows, setStopRows] = useState(null);
    const [overrides, setOverrides] = useState([]);
    const [reload, setReload] = useState(0);
    // Inline edit state: { name, start: 'HH:mm', end: 'HH:mm' } or null.
    const [editing, setEditing] = useState(null);
    const [saveError, setSaveError] = useState('');
    const userRole = localStorage.getItem('userRole');
    const canOverride = userRole === 'manager' || userRole === 'admin';

    useEffect(() => {
        if (!bookingId || !bookingStart) return undefined;
        const day = DateTime.fromISO(bookingStart, { zone: 'utc' })
            .setZone('Asia/Dubai')
            .toFormat('yyyy-MM-dd');
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`/api/routing/${day}/stops`);
                if (cancelled) return;
                setStopRows((res.data.stops || []).filter((s) => s.booking_id === Number(bookingId)));
            } catch (e) {
                // Routing is additive — without it the shared window stands.
                if (!cancelled) setStopRows([]);
            }
            try {
                const ov = await axios.get(`/api/bookings/${bookingId}/cleaner-times`);
                if (!cancelled) setOverrides(ov.data.overrides || []);
            } catch (e) {
                if (!cancelled) setOverrides([]);
            }
        })();
        return () => { cancelled = true; };
    }, [bookingId, bookingStart, reload]);

    // The table renders as soon as the cleaning STARTS (owner, 2026-09-03):
    // during In Process each row shows who was dropped when and counts from
    // when — the cells that need the finish simply say so. A booking that
    // never started still shows nothing.
    const TOLERANCE_MIN = 15;
    const WALK_MIN = 10;

    const computed = useMemo(() => {
        const ongoing = !finishedIso && !!isInProcess;
        if (!startedIso || (!finishedIso && !ongoing)) return null;
        if (stopRows === null) return null; // stops still loading

        const base0 = DateTime.fromISO(startedIso, { zone: 'utc' });
        const base1 = finishedIso ? DateTime.fromISO(finishedIso, { zone: 'utc' }) : null;
        if (!base0.isValid) return null;
        if (base1 && (!base1.isValid || base1 <= base0)) return null;

    // The cleaners on the booking, matched to their stops by name.
    const names = String(cleanerNames || '')
        .split(',')
        .map((n) => n.trim())
        .filter((n) => n && !/^no cleaners/i.test(n));

    const stopsByName = new Map();
    (stopRows || []).forEach((s) => {
        if (!s.cleaner_id) return;
        const name = [s.cleaner_first_name, s.cleaner_last_name].filter(Boolean).join(' ');
        const row = stopsByName.get(name) || { droppedAt: null, collectedAt: null };
        if (s.status === 'done') {
            if (s.stop_type === 'dropoff') row.droppedAt = s.completed_at;
            if (s.stop_type === 'collect') row.collectedAt = s.completed_at;
        }
        stopsByName.set(name, row);
    });

    /* Roster = the ASSIGNED cleaners plus anyone the driver ledger recorded
       on this booking (owner, 2026-09-03: a lady who worked an hour and was
       then moved to another rota must keep her hour — removal from the
       assignment must not erase the record). */
    const matchesAssigned = (full) => names.some((n) =>
        full === n || full.startsWith(n + ' ') || full.split(' ')[0] === n);
    const extraFromStops = [...stopsByName.keys()].filter((full) => !matchesAssigned(full));
    const roster = [...names, ...extraFromStops];
    if (roster.length === 0) return null;

    /* The booking lists cleaners by FIRST name; the driver ledger carries
       full names. Match exact first, then by first-name prefix — an
       ambiguous first name (two Marias on one booking) matches nobody
       rather than the wrong one. */
    const findStops = (name) => {
        if (stopsByName.has(name)) return stopsByName.get(name);
        const hits = [...stopsByName.entries()].filter(([full]) =>
            full === name || full.startsWith(name + ' ') || full.split(' ')[0] === name);
        return hits.length === 1 ? hits[0][1] : {};
    };

    const perCleaner = roster.map((name) => {
        const st = findStops(name) || {};
        let start = base0;
        let end = base1;
        let adjusted = false;

        if (st.droppedAt) {
            const droppedPlusWalk = DateTime.fromISO(st.droppedAt, { zone: 'utc' }).plus({ minutes: WALK_MIN });
            if (droppedPlusWalk > base0.plus({ minutes: TOLERANCE_MIN })) {
                start = droppedPlusWalk;
                adjusted = true;
            }
        }
        if (st.collectedAt) {
            const collected = DateTime.fromISO(st.collectedAt, { zone: 'utc' });
            if (base1) {
                if (collected < base1.minus({ minutes: TOLERANCE_MIN }) && collected > start) {
                    end = collected;
                    adjusted = true;
                }
            } else if (collected > start) {
                // Mid-cleaning, but SHE is already collected — her clock is done.
                end = collected;
                adjusted = true;
            }
        }
        // MANUAL OVERRIDE WINS (owner, 2026-09-03): an admin-pinned time
        // replaces the computed clock entirely for that side.
        let manual = false; let manualStart = false; let manualEnd = false;
        const ov = (overrides || []).find((o) => o.cleaner_name === name);
        if (ov?.start_at) {
            const os = DateTime.fromISO(ov.start_at, { zone: 'utc' });
            if (os.isValid) { start = os; manual = true; manualStart = true; }
        }
        if (ov?.end_at) {
            const oe = DateTime.fromISO(ov.end_at, { zone: 'utc' });
            if (oe.isValid) { end = oe; manual = true; manualEnd = true; }
        }
        const minutes = end ? Math.max(0, Math.round(end.diff(start, 'minutes').minutes)) : null;
        return { name, start, end, minutes, adjusted: adjusted && !manual, manual, manualStart, manualEnd,
            manualBy: ov?.set_by_name || null,
            droppedAt: st.droppedAt || null, collectedAt: st.collectedAt || null };
    });

    const allDone = perCleaner.every((c) => c.minutes !== null);
    const totalMinutes = perCleaner.reduce((sum, c) => sum + (c.minutes || 0), 0);
        return { perCleaner, allDone, totalMinutes };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startedIso, finishedIso, isInProcess, stopRows, cleanerNames, overrides]);

    // The header reads this table (owner, 2026-09-03 / 2026-09-09): the
    // combined figure once every clock has stopped, and ALWAYS the earliest
    // cleaner start and the latest cleaner finish — including hand-set ones,
    // with who set them — so "Actual start" cannot contradict the row below.
    useEffect(() => {
        if (!onCombined) return;
        if (!computed || !computed.perCleaner.length) { onCombined(null); return; }
        const withStart = computed.perCleaner.filter((c) => c.start && c.start.isValid);
        const earliest = withStart.reduce((a, c) => (!a || c.start < a.start ? c : a), null);
        const finished = computed.perCleaner.filter((c) => c.end && c.end.isValid);
        const latest = computed.allDone ? finished.reduce((a, c) => (!a || c.end > a.end ? c : a), null) : null;
        onCombined({
            minutes: computed.allDone ? computed.totalMinutes : null,
            cleaners: computed.perCleaner.length,
            start: earliest ? earliest.start.toUTC().toISO() : null,
            startManualBy: earliest && earliest.manualStart ? (earliest.manualBy || 'staff') : null,
            end: latest ? latest.end.toUTC().toISO() : null,
            endManualBy: latest && latest.manualEnd ? (latest.manualBy || 'staff') : null,
        });
    }, [computed, onCombined]);

    if (!computed) return null;
    const { perCleaner, allDone, totalMinutes } = computed;

    const fmtDur = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;

    // The booking's Dubai date anchors the HH:mm inputs.
    const dubaiDay = DateTime.fromISO(startedIso || bookingStart, { zone: 'utc' }).setZone('Asia/Dubai').toFormat('yyyy-MM-dd');
    const toIso = (hhmm) => (hhmm
        ? DateTime.fromFormat(`${dubaiDay} ${hhmm}`, 'yyyy-MM-dd HH:mm', { zone: 'Asia/Dubai' }).toUTC().toISO()
        : null);
    const beginEdit = (c) => {
        setSaveError('');
        setEditing({
            name: c.name,
            start: c.start ? c.start.setZone('Asia/Dubai').toFormat('HH:mm') : '',
            end: c.end ? c.end.setZone('Asia/Dubai').toFormat('HH:mm') : '',
        });
    };
    const saveEdit = async (clear = false) => {
        try {
            setSaveError('');
            await axios.put(`/api/bookings/${bookingId}/cleaner-times`, clear
                ? { cleaner_name: editing.name, start_at: null, end_at: null }
                : { cleaner_name: editing.name, start_at: toIso(editing.start), end_at: toIso(editing.end) });
            setEditing(null);
            setReload((n) => n + 1);
        } catch (e) {
            setSaveError(e.response?.data?.error || 'Could not save the time.');
        }
    };
    const fmtT = (dt) => dt.setZone('Asia/Dubai').toFormat('HH:mm');
    const fmtIso = (iso) => (iso
        ? DateTime.fromISO(iso, { zone: 'utc' }).setZone('Asia/Dubai').toFormat('HH:mm')
        : '—');

    /* ONE table, names once (owner, 2026-09-02: the same name three times —
       hours row, assigned list, stops line — was a mess). Each lady: when the
       van dropped and collected her, the window that COUNTS, and her hours. */
    return (
        <div className="dp-subSection">
            {/* No title — the table's own header row says it (owner). */}
            <table className="cd-table">
                <thead>
                    <tr>
                        <th>Cleaner</th>
                        <th>Dropped off</th>
                        <th>Start time</th>
                        <th>Finish time</th>
                        <th>Picked up</th>
                        <th>Counted</th>
                        <th>
                            Hours
                            <span
                                className="cd-info"
                                data-tooltip-id="cd-hours-info"
                                data-tooltip-content={`How the hours are counted, per cleaner:
• The clock runs over the counted window — actual start to actual finish, adjusted by the driver ledger.
• Dropped off more than ${TOLERANCE_MIN} min AFTER the start → her clock starts at drop-off + ${WALK_MIN} min (the walk from the van).
• Picked up more than ${TOLERANCE_MIN} min BEFORE the finish → her clock stops at the pickup.
• Within ${TOLERANCE_MIN} min the shared window stands — vans idle, clocks drift.
A * marks an adjusted row. Combined = the sum of every cleaner's counted hours.
The ✎ button on a row lets managers and admins edit that cleaner's actual start and finish; ✎ next to the hours marks a manually set row.`}
                            >i</span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {perCleaner.map((c) => (
                        editing && editing.name === c.name ? (
                            <tr key={c.name} className="cd-editRow">
                                <td className="cd-name">{c.name}</td>
                                <td>{fmtIso(c.droppedAt)}</td>
                                <td>
                                    <input type="time" className="cd-timeInput" value={editing.start}
                                        onChange={(e) => setEditing((v) => ({ ...v, start: e.target.value }))} />
                                </td>
                                <td>
                                    <input type="time" className="cd-timeInput" value={editing.end}
                                        onChange={(e) => setEditing((v) => ({ ...v, end: e.target.value }))} />
                                </td>
                                <td>{c.collectedAt ? fmtIso(c.collectedAt) : '—'}</td>
                                <td colSpan={2} className="cd-editActions">
                                    <button type="button" className="cd-btn cd-btn--save" onClick={() => saveEdit(false)}>Save</button>
                                    {c.manual && (
                                        <button type="button" className="cd-btn" title="Back to the computed clock" onClick={() => saveEdit(true)}>Reset</button>
                                    )}
                                    <button type="button" className="cd-btn" onClick={() => setEditing(null)}>Cancel</button>
                                    {saveError && <span className="cd-editError">{saveError}</span>}
                                </td>
                            </tr>
                        ) : (
                        <tr key={c.name}>
                            <td className="cd-name">{c.name}</td>
                            <td>{fmtIso(c.droppedAt)}</td>
                            <td>{fmtT(c.start)}</td>
                            <td>{c.end ? fmtT(c.end) : <span className="dp-muted">still in progress</span>}</td>
                            <td>{c.collectedAt ? fmtIso(c.collectedAt)
                                : (c.end ? '—' : <span className="dp-muted">still in progress</span>)}</td>
                            <td>{fmtT(c.start)} → {c.end ? fmtT(c.end) : <span className="dp-muted">…</span>}</td>
                            <td className="cd-dur">
                                {c.minutes === null
                                    ? <span className="dp-muted">still in progress</span>
                                    : fmtDur(c.minutes)}
                                {c.manual && (
                                    <span className="dp-muted" title={`Start/finish set manually by ${c.manualBy || 'staff'} — the computed clock is overridden.`}> ✎</span>
                                )}
                                {c.adjusted && c.minutes !== null && (
                                    <span
                                        className="dp-muted"
                                        title={`Adjusted from the driver ledger — drop-offs more than ${TOLERANCE_MIN} min after the start count from drop-off + ${WALK_MIN} min; early pickups stop the clock.`}
                                    > *</span>
                                )}
                                {canOverride && (
                                    <button
                                        type="button"
                                        className="cd-editBtn"
                                        data-tooltip-id="cd-hours-info"
                                        data-tooltip-content="Edit the cleaner's actual start and finish time"
                                        onClick={() => beginEdit(c)}
                                    >✎</button>
                                )}
                            </td>
                        </tr>
                        )
                    ))}
                    <tr className="cd-total">
                        <td colSpan={6}>Combined</td>
                        <td className="cd-dur">{allDone
                            ? fmtDur(totalMinutes)
                            : <span className="dp-muted">still in progress</span>}</td>
                    </tr>
                </tbody>
            </table>
            {/* Same tooltip the bookings list uses — plus pre-line so the
                bullet lines actually break. */}
            <Tooltip id="cd-hours-info" place="top" className="custom-tooltip cd-info-ttip" />
        </div>
    );
}
